import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Link,
  Divider,
  IconButton,
} from "@mui/material";
import { X, ExternalLink } from "lucide-react";

interface WorkProfile {
  public_identifier: string;
  profile_pic_url?: string;
  first_name: string;
  last_name: string;
  full_name: string;
  occupation: string;
  headline: string;
  summary: string;
  country: string;
  city: string;
  state: string;
  follower_count: number;
  connections: number;
  experiences: Array<{
    title: string;
    company: string;
    location?: string;
    starts_at: { year?: number; month?: number };
    ends_at?: { year?: number; month?: number };
    description?: string;
  }>;
  education: Array<{
    school: string;
    degree_name?: string;
    field_of_study?: string;
    starts_at: { year?: number; month?: number };
    ends_at: { year?: number; month?: number };
  }>;
  skills: Array<{ name: string }>;
}

interface WorkProfileProps {
  workProfileJson: string;
  linkedinUrl: string;
}

const formatDate = (date: { year?: number; month?: number }) => {
  if (!date) return "";
  if (!date.year) return "";
  if (!date.month) return date.year.toString();
  const monthNames = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${monthNames[date.month]} ${date.year}`;
};

const WorkProfileAbbreviated: React.FC<{
  profile: WorkProfile;
  onShowAll: () => void;
}> = ({ profile, onShowAll }) => {
  const currentExperience = profile.experiences?.[0];
  const topSkills = profile.skills?.slice(0, 3) || [];

  return (
    <Box>
      <Typography variant="bodyHeavy" mb={1}>
        LinkedIn Profile
      </Typography>
      <Box
        onClick={onShowAll}
        sx={{
          border: "1px solid #E2E3E4",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "white",
          minWidth: "280px",
          cursor: "pointer",
          position: "relative",
          "&:hover": {
            backgroundColor: "#F5F5F5",
          },
        }}
      >
        <Box display="flex" alignItems="center" gap="8px" mb={1}>
          <Avatar
            src={profile.profile_pic_url}
            alt={profile.full_name}
            sx={{ width: 32, height: 32 }}
          />
          <Box flex={1}>
            <Typography variant="bodyHeavy" fontSize={14}>
              {profile.full_name}
            </Typography>
            <Typography variant="body" color="text.secondary" fontSize={12}>
              {profile.headline}
            </Typography>
          </Box>
        </Box>

        {currentExperience && (
          <Box mb={1}>
            <Typography variant="body" fontSize={12} color="text.secondary">
              {currentExperience.title} at {currentExperience.company}
            </Typography>
          </Box>
        )}
        {topSkills.length > 0 ||
          (profile.skills.length > 0 && (
            <Box display="flex" gap="4px" mb={2} flexWrap="wrap">
              {topSkills.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill.name}
                  size="small"
                  sx={{
                    fontSize: "10px",
                    height: "20px",
                    backgroundColor: "#E3F2FD",
                    color: "#1976D2",
                  }}
                />
              ))}
              {profile.skills && profile.skills.length > 3 && (
                <Chip
                  label={`+${profile.skills.length - 3} more`}
                  size="small"
                  sx={{
                    fontSize: "10px",
                    height: "20px",
                    backgroundColor: "#F5F5F5",
                    color: "#666",
                  }}
                />
              )}
            </Box>
          ))}

        <Box display="flex" justifyContent="flex-end">
          <Button
            size="small"
            variant="text"
            sx={{
              height: "unset",
              fontSize: "11px",
              padding: "4px 8px",
              color: "text.secondary",
              textTransform: "none",
            }}
          >
            View full profile
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const WorkProfileDialog: React.FC<{
  profile: WorkProfile;
  open: boolean;
  onClose: () => void;
  linkedinUrl: string;
}> = ({ profile, open, onClose, linkedinUrl }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">LinkedIn Profile</Typography>
          <IconButton onClick={onClose} size="small">
            <X />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap="20px">
          {/* Header */}
          <Box display="flex" gap="16px" alignItems="start">
            <Avatar
              src={profile.profile_pic_url}
              alt={profile.full_name}
              sx={{ width: 80, height: 80 }}
            />
            <Box flex={1}>
              <Typography variant="h5" mb={1}>
                {profile.full_name}
              </Typography>
              <Typography variant="body" color="text.secondary" mb={1}>
                {profile.headline}
              </Typography>
              <Typography variant="body" color="text.secondary" fontSize={14}>
                {profile.city}, {profile.state}, {profile.country}
              </Typography>
              <Box display="flex" gap="16px" mt={1}>
                <Typography variant="body" fontSize={14}>
                  {profile.connections} connections
                </Typography>
                <Typography variant="body" fontSize={14}>
                  {profile.follower_count} followers
                </Typography>
              </Box>
            </Box>
            <Link
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <ExternalLink size={16} />
              View on LinkedIn
            </Link>
          </Box>

          {/* Summary */}
          {profile.summary && (
            <>
              <Divider />
              <Box>
                <Typography variant="bodyHeavy" mb={1}>
                  About
                </Typography>
                <Typography variant="body" whiteSpace="pre-wrap">
                  {profile.summary}
                </Typography>
              </Box>
            </>
          )}

          {/* Experience */}
          {profile.experiences && profile.experiences.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="bodyHeavy" mb={2}>
                  Experience
                </Typography>
                {profile.experiences.map((exp, index) => (
                  <Box key={index} mb={2}>
                    <Typography variant="bodyHeavy" fontSize={16}>
                      {exp.title}
                    </Typography>
                    <Typography variant="body" color="text.secondary">
                      {exp.company} {exp.location && `• ${exp.location}`}
                    </Typography>
                    <Typography
                      variant="body"
                      color="text.secondary"
                      fontSize={14}
                    >
                      {exp.starts_at && `${formatDate(exp.starts_at)} -  `}
                      {exp.ends_at ? formatDate(exp.ends_at) : "Present"}
                    </Typography>
                    {exp.description && (
                      <Typography variant="body" mt={1} whiteSpace="pre-wrap">
                        {exp.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Education */}
          {profile.education && profile.education.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="bodyHeavy" mb={2}>
                  Education
                </Typography>
                {profile.education.map((edu, index) => (
                  <Box key={index} mb={2}>
                    <Typography variant="bodyHeavy" fontSize={16}>
                      {edu.school}
                    </Typography>
                    <Typography variant="body" color="text.secondary">
                      {edu.degree_name}{" "}
                      {edu.field_of_study && `in ${edu.field_of_study}`}
                    </Typography>
                    <Typography
                      variant="body"
                      color="text.secondary"
                      fontSize={14}
                    >
                      {formatDate(edu.starts_at)} - {formatDate(edu.ends_at)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Skills */}
          {profile.skills && profile.skills.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="bodyHeavy" mb={2}>
                  Skills
                </Typography>
                <Box display="flex" flexWrap="wrap" gap="8px">
                  {profile.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill.name}
                      size="small"
                      sx={{
                        backgroundColor: "#E3F2FD",
                        color: "#1976D2",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ({ workProfileJson, linkedinUrl }: WorkProfileProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!workProfileJson) {
    return null;
  }

  let profile: WorkProfile;
  try {
    profile = JSON.parse(workProfileJson);
  } catch (error) {
    console.error("Failed to parse work profile JSON:", error);
    return null;
  }

  return (
    <>
      <WorkProfileAbbreviated
        profile={profile}
        onShowAll={() => setDialogOpen(true)}
      />
      <WorkProfileDialog
        profile={profile}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        linkedinUrl={linkedinUrl}
      />
    </>
  );
};
